import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { Modal as BSModal } from 'react-bootstrap';
import { Typography, NewIconButton } from 'app/components';
import { DateTimePicker, Localization } from 'react-widgets';
import type { DateLocalizer } from 'react-widgets';
import MomentLocalizer from 'react-widgets-moment';
import moment from 'moment';

import './Modal.scss';

moment.locale('sv');

type MomentLocalizerCtor = {
  (): void;
  new (m: unknown): DateLocalizer<unknown>;
};

const localizer = new (MomentLocalizer as MomentLocalizerCtor)(moment);

interface AddDateModalProps {
  show: boolean;
  editData?: {
    id: string;
    type: 'period' | 'instant' | 'forever';
    fromDate?: string;
    toDate?: string;
    date?: string;
  };
  hasForeverDate: boolean;
  onClose: () => void;
  onAdd: (dateData: { type: 'period' | 'instant' | 'forever'; fromDate?: Date; toDate?: Date; date?: Date }) => void;
  onEdit?: (id: string, dateData: { type: 'period' | 'instant' | 'forever'; fromDate?: Date; toDate?: Date; date?: Date }) => void;
}

export const AddDateModal = ({ show, editData, hasForeverDate, onClose, onAdd, onEdit }: AddDateModalProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const isEditMode = !!editData;
  const isEditingForeverDate = isEditMode && editData?.type === 'forever';

  const [dateType, setDateType] = useState<'period' | 'instant' | 'forever'>('period');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [instantDate, setInstantDate] = useState<Date | null>(null);

  const dateTypesAvailable: { value: 'period' | 'instant' | 'forever'; label: string }[] = [
    {
      value: 'period',
      label: t('tag:period.title'),
    },
    {
      value: 'instant',
      label: t('tag:instant.label'),
    },
    {
      value: 'forever',
      label: t('tag:forever.label'),
    },
  ];

  const resetForm = useCallback(() => {
    setDateType('period');
    setFromDate(null);
    setToDate(null);
    setInstantDate(null);
  }, []);

  // Reset form or pre-fill when modal opens/closes or editData changes
  useEffect(() => {
    if (show && editData) {
      setDateType(editData.type);
      setFromDate(editData.fromDate ? new Date(editData.fromDate) : null);
      setToDate(editData.toDate ? new Date(editData.toDate) : null);
      setInstantDate(editData.date ? new Date(editData.date) : null);
    } else if (!show) {
      resetForm();
    }
  }, [show, editData, resetForm]);

  const buildDateData = () => {
    if (dateType === 'period' && fromDate && toDate) {
      return { type: 'period' as const, fromDate, toDate };
    }
    if (dateType === 'instant' && instantDate) {
      return { type: 'instant' as const, date: instantDate };
    }
    if (dateType === 'forever') {
      return { type: 'forever' as const };
    }
    return null;
  };

  const isValid = dateType === 'forever' || (dateType === 'period' && fromDate && toDate && fromDate <= toDate) || (dateType === 'instant' && instantDate);

  const handleAdd = () => {
    const dateData = buildDateData();
    if (!dateData) return;

    if (isEditMode && onEdit && editData) {
      onEdit(editData.id, dateData);
    } else {
      onAdd(dateData);
    }

    resetForm();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) handleAdd();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BSModal show={show} onHide={handleClose} contentClassName="taxonomy-modal">
      <BSModal.Body>
        <form onSubmit={handleSubmit} className="taxonomy-modal__content">
          <div className="taxonomy-modal__header">
            <Typography variant="body-8" fontWeight="semibold" className="taxonomy-modal__title">
              {isEditMode ? t('common:edit') : t('common:add')} {t('tag:date.label')}
            </Typography>

            <NewIconButton icon="close" variant="transparent" type="button" onClick={handleClose} />
          </div>

          {!isEditMode && (
            <div className="form-group">
              <Typography variant="body-7" fontWeight="semibold">
                {t('tag:type')}
              </Typography>

              {dateTypesAvailable.map((type) => {
                if (type.value === 'forever' && hasForeverDate && !isEditingForeverDate) return null;

                return (
                  <label key={type.value} className="radio-label">
                    <input type="radio" name="date-type" value={type.value} checked={dateType === type.value} onChange={() => setDateType(type.value)} />
                    {type.label}
                  </label>
                );
              })}
            </div>
          )}

          {dateType === 'period' && (
            <div className="date-inputs">
              <div className="form-group">
                <Typography variant="body-7" fontWeight="semibold">
                  {t('tag:from.label')}
                </Typography>

                <Localization date={localizer}>
                  <DateTimePicker
                    containerClassName="date-picker"
                    selectIcon={<i className="fa-regular fa-calendar" />}
                    onChange={(date) => setFromDate(date || null)}
                    value={fromDate}
                    max={toDate || undefined}
                    placeholder="YYYY-MM-DD"
                  />
                </Localization>
              </div>

              <div className="form-group">
                <Typography variant="body-7" fontWeight="semibold">
                  {t('tag:to.label')}
                </Typography>
                <Localization date={localizer}>
                  <DateTimePicker
                    containerClassName="date-picker"
                    selectIcon={<i className="fa-regular fa-calendar" />}
                    onChange={(date) => setToDate(date || null)}
                    value={toDate}
                    min={fromDate || undefined}
                    placeholder="YYYY-MM-DD"
                  />
                </Localization>
              </div>
            </div>
          )}

          {dateType === 'instant' && (
            <div className="form-group">
              <Typography variant="body-7" fontWeight="semibold">
                {t('tag:date.label')}
              </Typography>
              <Localization date={localizer}>
                <DateTimePicker
                  containerClassName="date-picker"
                  selectIcon={<i className="fa-regular fa-calendar" />}
                  onChange={(date) => setInstantDate(date || null)}
                  value={instantDate}
                  placeholder="YYYY-MM-DD"
                />
              </Localization>
            </div>
          )}

          {dateType === 'forever' && (
            <div className="info-message">
              <i className="fa-regular fa-info-circle" />
              <Typography variant="body-7">{t('tag:forever.description')}</Typography>
            </div>
          )}

          <div className="taxonomy-modal__footer">
            <button type="submit" className="btn btn-primary" disabled={!isValid}>
              {isEditMode ? t('common:save') : t('common:add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              {t('common:cancel')}
            </button>
          </div>
        </form>
      </BSModal.Body>
    </BSModal>
  );
};
